import { useEffect } from "react";
import { WorkOrder } from "@myapp/contract";
import { FieldWrapper, Input } from "../../../../shared/ui";
import { FormSection } from "../../../../shared/ui/controls/FormSection/FormSection";
import { getErrorMessage } from "../../../../shared/helpers/getErrorMessage";
import { useImmotionAuftrag } from "../model/useImmotionAuftrag";
import { ImmotionAttachmentsGallery } from "../../immotion-attachments";

type ImmotionAuftragSectionProps = {
  immotionAuftragId: string;
  onImmotionAuftragIdChange?: (value: string) => void;
  immotionAuftragIdError?: string;
  onAuftragLoad?: (workOrder: WorkOrder | undefined) => void;
  readonly?: boolean;
};

export const ImmotionAuftragSection = ({
  immotionAuftragId,
  onImmotionAuftragIdChange,
  immotionAuftragIdError,
  onAuftragLoad,
  readonly = false,
}: ImmotionAuftragSectionProps) => {
  const { immotionFields, workOrder, isFetching, error, handleLoadAuftrag } = useImmotionAuftrag(immotionAuftragId);

  useEffect(() => {
    onAuftragLoad?.(workOrder);
  }, [workOrder]);

  useEffect(() => {
    if (readonly && immotionAuftragId) {
      handleLoadAuftrag();
    }
  }, [readonly, immotionAuftragId]);
  const inputError = immotionAuftragIdError ?? (error ? getErrorMessage(error) : undefined);

  return (
    <FormSection title="Immotion Auftrag">
      {!readonly && (
        <div className="mb-2 flex flex-col items-stretch gap-3 sm:flex-row sm:items-end sm:gap-[24px]">
          <FieldWrapper label="Auftrags-ID aus Immotion" className="flex-1 min-w-0">
            <Input
              value={immotionAuftragId}
              onChange={onImmotionAuftragIdChange}
              placeholder=""
              error={inputError}
            />
          </FieldWrapper>

          <button
            type="button"
            onClick={handleLoadAuftrag}
            disabled={isFetching}
            className="w-full self-stretch bg-[#124093] text-white font-['Roboto'] text-[17px] font-medium rounded-[40px] px-[16px] py-[17px] whitespace-nowrap hover:brightness-110 transition-all leading-none disabled:opacity-60 disabled:cursor-not-allowed sm:w-auto sm:self-end"
          >
            {isFetching ? "Lade..." : "Auftrag laden"}
          </button>
        </div>
      )}

      <div className="flex items-center gap-2 mb-4">
        <input
          type="checkbox"
          id="wohnungsmodernisierung"
          checked={immotionFields.isWohnungsmodernisierung}
          disabled
          className="w-4 h-4 accent-[#124093] cursor-not-allowed"
        />
        <label htmlFor="wohnungsmodernisierung" className="font-['Roboto'] text-[14px] text-[#333] select-none cursor-not-allowed">
          Wohnungsmodernisierung
        </label>
      </div>

      {/* One column on a phone, two on a tablet. At three the fields are ~100 px wide — narrower
          than the labels above them. */}
      <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3 lg:gap-x-10 lg:gap-y-6">
        <FieldWrapper label="Wirtschaftseinheit">
          <Input value={immotionFields.wirtschaftseinheit} disabled placeholder="" />
        </FieldWrapper>

        <FieldWrapper label="Mieter">
          <Input value={immotionFields.mieter} disabled placeholder="" as="textarea" inputClassName="min-h-[90px]" />
        </FieldWrapper>

        <FieldWrapper label="Erfasser">
          <Input value={immotionFields.erfasser} disabled placeholder="" />
        </FieldWrapper>

        <FieldWrapper label="Gebäude">
          <Input value={immotionFields.hauseingang} disabled placeholder="" />
        </FieldWrapper>

        <FieldWrapper label="Gewerk">
          <Input value={immotionFields.gewerk} disabled placeholder="" />
        </FieldWrapper>

        {/* The double-height cell only pays off once the grid actually has three columns to
            balance — stacked, it would just leave a gap. */}
        <FieldWrapper label="Schadensbeschreibung" className="lg:row-span-2 lg:h-full">
          <Input
            value={immotionFields.schadensbeschreibung}
            as="textarea"
            // `h-full` has nothing to fill once the cell stops spanning two rows, so the box needs
            // a floor of its own or it collapses to one line on a phone.
            inputClassName="flex-1 h-full min-h-[90px]"
            className="h-full"
            disabled
            placeholder=""
          />
        </FieldWrapper>

        <FieldWrapper label="Wohnung">
          <Input value={immotionFields.wohnung} disabled placeholder="" />
        </FieldWrapper>
        

        <FieldWrapper label="Leistung">
          <Input value={`${immotionFields.leistung} ${workOrder?.external.infoText ? `(${workOrder?.external.infoText})` : ''}`} disabled placeholder="" />
        </FieldWrapper>{/* 20263958 */}


      </div>

      <ImmotionAttachmentsGallery
        attachments={workOrder?.external.immotionAttachments}
        className="mt-6"
      />
    </FormSection>
  );
};
